import apiService from './api';
import type { ApiResponse } from '../types/api';
import type {
  CultivarSynonym, SynonymFormData,
  ReleaseState,
  CultivarRegionRelease,
  RegionLicensorGrant, RegionLicensorState, CreateGrantRequest,
} from '../types/breeder';

class BreederService {
  // --- Synonyms ---
  async listSynonyms(cultivarId: number): Promise<ApiResponse<CultivarSynonym[]>> {
    return apiService.get(`/cultivar/registry/${cultivarId}/synonyms`);
  }

  async createSynonym(cultivarId: number, data: SynonymFormData): Promise<ApiResponse<CultivarSynonym>> {
    return apiService.post(`/cultivar/registry/${cultivarId}/synonyms`, data);
  }

  async updateSynonym(
    cultivarId: number, synonymId: number, data: Partial<SynonymFormData>
  ): Promise<ApiResponse<CultivarSynonym>> {
    return apiService.put(`/cultivar/registry/${cultivarId}/synonyms/${synonymId}`, data);
  }

  async deleteSynonym(cultivarId: number, synonymId: number): Promise<ApiResponse<void>> {
    return apiService.delete(`/cultivar/registry/${cultivarId}/synonyms/${synonymId}`);
  }

  // --- Release state ---
  async setReleaseState(
    cultivarId: number,
    release_state: ReleaseState,
    primary_display_synonym_id?: number | null
  ): Promise<ApiResponse<{ id: number; release_state: ReleaseState; primary_display_synonym_id: number | null }>> {
    const body: Record<string, unknown> = { release_state };
    if (primary_display_synonym_id !== undefined) {
      body.primary_display_synonym_id = primary_display_synonym_id;
    }
    return apiService.put(`/cultivar/registry/${cultivarId}/release-state`, body);
  }

  // --- Region releases ---
  async listRegionReleases(cultivarId: number): Promise<ApiResponse<CultivarRegionRelease[]>> {
    return apiService.get(`/cultivar/registry/${cultivarId}/region-releases`);
  }

  async upsertRegionRelease(
    cultivarId: number,
    region_code: string,
    data: { released_to_growers: boolean; public_synonym_id?: number | null; notes?: string | null }
  ): Promise<ApiResponse<CultivarRegionRelease>> {
    return apiService.put(`/cultivar/registry/${cultivarId}/region-releases/${region_code}`, data);
  }

  // --- Region licensor grants ---
  async listGrants(opts: { as?: 'breeder' | 'licensor'; state?: RegionLicensorState } = {}): Promise<ApiResponse<RegionLicensorGrant[]>> {
    const params = new URLSearchParams();
    if (opts.as) params.set('as', opts.as);
    if (opts.state) params.set('state', opts.state);
    const qs = params.toString();
    return apiService.get(`/breeder/region-licensors${qs ? `?${qs}` : ''}`);
  }

  async createGrant(data: CreateGrantRequest): Promise<ApiResponse<RegionLicensorGrant>> {
    return apiService.post('/breeder/region-licensors', data);
  }

  async approveGrant(id: number): Promise<ApiResponse<RegionLicensorGrant>> {
    return apiService.post(`/breeder/region-licensors/${id}/approve`, {});
  }

  async rejectGrant(id: number, notes?: string): Promise<ApiResponse<RegionLicensorGrant>> {
    return apiService.post(`/breeder/region-licensors/${id}/reject`, { notes });
  }

  async revokeGrant(id: number, notes?: string): Promise<ApiResponse<RegionLicensorGrant>> {
    return apiService.post(`/breeder/region-licensors/${id}/revoke`, { notes });
  }

  // --- Anonymised planting map ---
  async getPlantingsMap(cultivarId: number): Promise<ApiResponse<PlantingsMapResponse>> {
    return apiService.get(`/breeder/cultivars/${cultivarId}/plantings-map`);
  }
}

export interface PlantingMapPoint {
  area_id: number;
  boundary_geometry: { type: 'Polygon'; coordinates: number[][][] } | null;
  center_point: { lat: number; lng: number } | null;
  area_hectares: number | string;
  tree_count: number;
  planted_year: number | null;
  planting_date: string | null;
}

export interface PlantingsMapResponse {
  plantings: PlantingMapPoint[];
  by_year: Record<string, { plantings: number; trees: number; hectares: number }>;
}

export default new BreederService();
